import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/layout/PageHeader";
import { Loading } from "@/components/shared/Loading";
import { CommunicationTimeline } from "@/components/shared/CommunicationTimeline";
import { BusinessStage } from "@/components/shared/BusinessStage";
import { useInbox, useCustomers, useRole } from "@/api/hooks";
import { api } from "@/api/client";
import { Mail, MessageCircle, Phone, Layers } from "lucide-react";

const FILTERS = [
  { id: "all", label: "All", icon: Layers },
  { id: "email", label: "Email", icon: Mail },
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircle },
  { id: "call", label: "Calls", icon: Phone },
];

export default function SharedInbox() {
  const [channel, setChannel] = useState("all");
  const [customerId, setCustomerId] = useState("");
  const { data: messages } = useInbox(channel, customerId);
  const { data: customers } = useCustomers();
  const navigate = useNavigate();
  const role = useRole();
  const qc = useQueryClient();

  // Item 4 — new-message alert: a real, non-decorative count of unread
  // inbound messages in the current filtered view.
  const unreadCount = (messages || []).filter((m) => m.dir === "in" && !m.readAt).length;

  const markRead = (m) => {
    if (m.dir === "in" && !m.readAt) {
      api.post(`/messages/${m.id}/read`).then(() => qc.invalidateQueries({ queryKey: ["inbox"] })).catch(() => {});
    }
    if (m.customerId) navigate(`/${role}/customers/${m.customerId}`);
  };

  return (
    <>
      <PageHeader title="Messages" subtitle={`One shared history across Email · WhatsApp · Phone${unreadCount ? ` · ${unreadCount} new` : ""}`}
        actions={
          <div className="flex items-center gap-2">
            <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} data-testid="inbox-client-filter"
              className="h-8 rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-600">
              <option value="">All clients</option>
              {(customers || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-0.5">
              {FILTERS.map((f) => (
                <button key={f.id} onClick={() => setChannel(f.id)} data-testid={`inbox-filter-${f.id}`}
                  className={`flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${channel === f.id ? "bg-indigo-600 text-white" : "text-slate-500 hover:bg-slate-50"}`}>
                  <f.icon className="h-3.5 w-3.5" /> {f.label}
                </button>
              ))}
            </div>
          </div>
        } />
      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        {!messages ? <Loading /> : messages.length === 0 ? (
          <div className="text-sm text-slate-400 p-4">No messages for this filter.</div>
        ) : (
          <div className="mx-auto max-w-5xl space-y-3">
            {messages.map((m) => (
              <div key={m.id} data-testid={`inbox-msg-${m.id}`} onClick={() => markRead(m)} className="cursor-pointer">
                <div className="text-[11px] font-semibold text-slate-400 mb-1 ml-1 flex items-center gap-1.5">
                  {m.dir === "in" && !m.readAt && <span className="h-1.5 w-1.5 rounded-full bg-indigo-600 shrink-0" data-testid={`inbox-unread-${m.id}`} title="Unread" />}
                  {m.customerName}{m.source && <span className="ml-1.5 font-normal text-slate-300">· {m.source}</span>}
                </div>
                {m.businessStage && (
                  <div className="mb-2 ml-1 flex min-w-0 items-center gap-2">
                    <BusinessStage stage={m.businessStage} compact />
                    <span className="truncate text-[11px] text-slate-500">{m.businessStage.name} · Last status: {m.businessStage.lastStatus}</span>
                  </div>
                )}
                <CommunicationTimeline threads={[m]} />
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
